import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslations } from '../i18n';

export function FloatingContactButton() {
  const { pathname } = useLocation();
  const { t } = useTranslations();
  const label = t('contact.floatingCta', 'Make a booking');
  const linkTarget = pathname === '/' ? { hash: '#book-table' } : { pathname: '/', hash: '#book-table' };
  const [isAtTop, setIsAtTop] = useState(true);
  const [isBookingSectionVisible, setIsBookingSectionVisible] = useState(false);
  const [backToTopActive, setBackToTopActive] = useState(false);
  const backToTopTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const nearTop = window.scrollY < 120;
      setIsAtTop(nearTop);

      if (nearTop && backToTopActive) {
        setBackToTopActive(false);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (pathname !== '/') {
      setIsBookingSectionVisible(false);
      return;
    }

    const target = document.getElementById('book-table');
    if (!target) {
      setIsBookingSectionVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        setIsBookingSectionVisible(visible);
      },
      {
        root: null,
        threshold: 0.2,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [pathname]);

  const isHome = pathname === '/';
  const showBackToTop = !isAtTop && !backToTopActive;
  const showBookingCta = isHome ? !(isAtTop || isBookingSectionVisible) : true;

  const handleBackToTop = () => {
    setBackToTopActive(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (backToTopTimerRef.current !== null) {
      window.clearTimeout(backToTopTimerRef.current);
    }
    backToTopTimerRef.current = window.setTimeout(() => {
      backToTopTimerRef.current = null;
      setBackToTopActive(false);
    }, 700);
  };

  useEffect(() => {
    return () => {
      if (backToTopTimerRef.current !== null) {
        window.clearTimeout(backToTopTimerRef.current);
      }
    };
  }, []);

  const handleBookingClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname === '/') {
      event.preventDefault();
      document.getElementById('book-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 md:bottom-8 md:right-8">
      <div
        className={`transition-all duration-300 ease-out ${
          showBackToTop
            ? 'pointer-events-auto opacity-100 translate-y-0 visible'
            : 'pointer-events-none opacity-0 translate-y-2 scale-95 invisible'
        }`}
        aria-hidden={!showBackToTop}
      >
        <button
          type="button"
          onClick={handleBackToTop}
          className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-[rgba(15,20,23,0.82)] text-white shadow-[0_20px_40px_-22px_rgba(0,0,0,0.65)] transition-colors duration-150 hover:bg-[var(--color-accent)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(194,103,58,0.4)] active:bg-[rgba(15,20,23,0.82)]"
          aria-label={t('common.backToTop', 'Back to top') as string}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10.53 2.47a.75.75 0 0 0-1.06 0l-5 5a.75.75 0 1 0 1.06 1.06l3.72-3.72V17a.75.75 0 0 0 1.5 0V4.81l3.72 3.72a.75.75 0 1 0 1.06-1.06l-5-5Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div
        className={`transition-all duration-300 ease-out ${
          showBookingCta
            ? 'pointer-events-auto opacity-100 translate-y-0 visible'
            : 'pointer-events-none opacity-0 translate-y-2 scale-95 invisible'
        }`}
        aria-hidden={!showBookingCta}
      >
        <Link
          to={linkTarget}
          onClick={handleBookingClick}
          className="pointer-events-auto glass-button glass-button--accent inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white shadow-[0_24px_58px_-28px_rgba(194,103,58,0.55)] transition-colors duration-150 hover:text-white focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-primary)]"
        >
          <span className="text-white">{label}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M3 10a.75.75 0 0 1 .75-.75h10.638l-4.22-4.22a.75.75 0 0 1 1.061-1.06l5.5 5.5a.75.75 0 0 1 0 1.06l-5.5 5.5a.75.75 0 1 1-1.06-1.06l4.22-4.22H3.75A.75.75 0 0 1 3 10Z"
              clipRule="evenodd"
            />
          </svg>
        </Link>
      </div>
    </div>
  );
}
